// aifab - Projects page
import { useState } from 'react';
import { useSubmit } from '../hooks/useSubmit';
import { useForm } from '../hooks/useForm';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, StatusBadge, EmptyState, Modal, ModalBody, ModalFooter, Err } from '../components/common';

export default function ProjectsPage({ projects: projectsProp, reloadProjects, projectId, onNavigate, onProjectChange }) {
  const data    = projectsProp ?? null;
  const loading  = projectsProp === null;
  const reload   = reloadProjects ?? (() => {});
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);   // project being edited
  const [deleting,setDeleting]= useState(null);   // project being deleted
  const [form,    setForm]    = useState({ project_name:'', client_name:'', description:'' });
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState(null);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const save = async () => {
    if (!form.project_name.trim()) { setErr('Project name is required'); return; }
    if (!form.client_name.trim())  { setErr('Client name is required');  return; }
    setSaving(true); setErr(null);
    try {
      await apiFetch('/projects', { method:'POST', body:JSON.stringify(form) });
      reload(); setShow(false);
      setForm({ project_name:'', client_name:'', description:'' });
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="page-hd">
        <div>
          <div className="page-sub">Organise models, experiments and client engagements</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ New Project</button>
      </div>

      {loading ? (
        <div className="flex" style={{ justifyContent:'center', padding:48 }}><Spinner size={28} /></div>
      ) : (data||[]).length === 0 ? (
        <EmptyState icon="⬡" title="No projects yet"
          subtitle="Create a project to start running models"
          action={<button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ New Project</button>} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:14 }}>
          {(data||[]).map(p => (
            <div key={p.id} className="card" style={{ transition:'box-shadow .12s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--sh2)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--sh1)'}
            >
              {/* Header - clickable to navigate */}
              <div className="card-hd" style={{ paddingBottom:12, cursor:'pointer' }}
                onClick={() => { onProjectChange?.(p.id); onNavigate?.('models'); }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#0F172A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {p.project_name}
                  </div>
                  <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>{p.client_name}</div>
                </div>
                <StatusBadge code={p.status} />
              </div>

              {/* Stats */}
              <div style={{ padding:'10px 20px 14px', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, cursor:'pointer' }}
                onClick={() => { onProjectChange?.(p.id); onNavigate?.('models'); }}>
                <div>
                  <div className="xs c-3" style={{ marginBottom:2, textTransform:'uppercase', letterSpacing:'.07em', fontWeight:600 }}>Last Run</div>
                  <div className="sm">{p.last_trained_at ? p.last_trained_at.slice(0,10) : '-'}</div>
                </div>
                <div>
                  <div className="xs c-3" style={{ marginBottom:2, textTransform:'uppercase', letterSpacing:'.07em', fontWeight:600 }}>Models</div>
                  <div className="sm bold">{p.models_count ?? 0}</div>
                </div>
                <div>
                  <div className="xs c-3" style={{ marginBottom:2, textTransform:'uppercase', letterSpacing:'.07em', fontWeight:600 }}>Created</div>
                  <div className="sm">{p.created_at?.slice(0,10)}</div>
                </div>
              </div>

              {/* Description */}
              {p.description && (
                <div style={{ padding:'0 20px 12px', fontSize:11, color:'#64748B', borderTop:'1px solid var(--bd)', paddingTop:10, lineHeight:1.5, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                  {p.description}
                </div>
              )}

              {/* Actions */}
              <div style={{ padding:'8px 16px 12px', borderTop:'1px solid var(--bd)', display:'flex', gap:6, justifyContent:'flex-end' }}>
                <button className="btn btn-ghost btn-xs"
                  onClick={() => { setEditing(p); setErr(null); }}>
                  ✎ Edit
                </button>
                <button className="btn btn-ghost btn-xs"
                  style={{ color:'var(--red)' }}
                  onClick={() => { setDeleting(p); setErr(null); }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── New Project Modal ── */}
      {show && (
        <Modal title="New Project" subtitle="Create a project to organise models, runs and data" onClose={() => setShow(false)}>
          <ModalBody>
            <Err msg={err} />
            <div className="form-row form-row-2">
              <div className="form-grp">
                <label className="form-lbl">Project Name *</label>
                <input className="form-inp" placeholder="e.g. ITSM Triage Classifier"
                  value={form.project_name} onChange={e => set('project_name', e.target.value)} />
              </div>
              <div className="form-grp">
                <label className="form-lbl">Client Name *</label>
                <input className="form-inp" placeholder="e.g. BDX Corp"
                  value={form.client_name} onChange={e => set('client_name', e.target.value)} />
              </div>
            </div>
            <div className="form-grp">
              <label className="form-lbl">Description</label>
              <textarea className="form-inp" rows={3} placeholder="What is this project solving?"
                value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-ghost btn-sm" onClick={() => setShow(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? <Spinner size={12} /> : 'Create Project'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* ── Edit Project Modal ── */}
      {editing && (
        <EditProjectModal
          project={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleting && (
        <DeleteProjectModal
          project={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditProjectModal({ project, onClose, onDone }) {
  const { run, saving, err } = useSubmit();
  const { form, set } = useForm({
    project_name: project.project_name || '',
    client_name:  project.client_name  || '',
    description:  project.description  || '',
  });

  const save = () => run(async () => {
    if (!form.project_name.trim()) throw new Error('Project name is required');
    if (!form.client_name.trim())  throw new Error('Client name is required');
    await apiFetch(`/projects/${project.id}`, { method:'PATCH', body:JSON.stringify(form) });
    onDone();
  });

  return (
    <Modal title="Edit Project" subtitle={project.project_name} onClose={onClose}>
      <ModalBody>
        <Err msg={err} />
        <div className="form-row form-row-2">
          <div className="form-grp">
            <label className="form-lbl">Project Name *</label>
            <input className="form-inp" value={form.project_name}
              onChange={e => set('project_name', e.target.value)} />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Client Name *</label>
            <input className="form-inp" value={form.client_name}
              onChange={e => set('client_name', e.target.value)} />
          </div>
        </div>
        <div className="form-grp">
          <label className="form-lbl">Description</label>
          <textarea className="form-inp" rows={3} value={form.description}
            onChange={e => set('description', e.target.value)} />
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12} /> : 'Save Changes'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function DeleteProjectModal({ project, onClose, onDone }) {
  const { run, saving, err } = useSubmit();

  const confirm = () => run(async () => {
    await apiFetch(`/projects/${project.id}`, { method:'DELETE' });
    onDone();
  });

  return (
    <Modal title="Delete Project" onClose={onClose}>
      <ModalBody>
        <Err msg={err} />
        <p style={{ fontSize:13, color:'#334155', marginBottom:12 }}>
          Are you sure you want to delete <strong style={{ color:'#0F172A' }}>{project.project_name}</strong>?
        </p>
        <p style={{ fontSize:12, color:'#64748B' }}>
          This can only be done if the project has no models, datasources, or runs.
          This action cannot be undone.
        </p>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-sm" style={{ background:'var(--red)', color:'#0F172A' }}
          onClick={confirm} disabled={saving}>
          {saving ? <Spinner size={12} /> : 'Delete Project'}
        </button>
      </ModalFooter>
    </Modal>
  );
}